import { colors } from '@ctw/config/zui';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { tableStyles } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-table-renderer';
import { isCdaDetailSubsectionTable } from '@ctw/shared/utils/cda/helpers';
import type { CdaDetailField, CdaDetailSubsection, CdaDocument } from '@ctw/shared/utils/cda/types';
import { formatDateToLocalString } from '@ctw/shared/utils/dates';
import { formatPhoneNumber } from '@ctw/shared/utils/phone-number';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { orderBy } from 'lodash-es';
import { memo, useMemo } from 'react';
import { headingStyles } from './pdf-heading-renderer';

interface PdfDocumentDetailsRendererProps {
  patient: PatientModel;
  cdaDocument: CdaDocument;
}

const styles = StyleSheet.create({
  detailItemsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 3,
  },
  detailItem: {
    width: '32%', // Three columns
    minHeight: 35,
    padding: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  detailItemLabel: {
    fontWeight: 500,
    fontSize: 12,
    letterSpacing: '0.05rem',
    color: colors.meta.text,
  },
  detailItemValue: {
    fontSize: 11,
  },
  detailsSubsectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
});

export const PdfDocumentDetailsRenderer = memo(
  ({ patient, cdaDocument }: PdfDocumentDetailsRendererProps) => {
    const detailSubsections: Array<CdaDetailSubsection> = useMemo(
      () => [
        ...cdaDocument.detailSubsections,
        {
          title: 'Patient Details',
          subtitle: `This section contains the most up-to-date patient information as of ${formatDateToLocalString(new Date())}`,
          rank: 1,
          fields: [
            {
              label: 'Patient Name',
              value: `${patient.fullName}${patient.gender?.[0] ? ` (${patient.gender?.[0].toUpperCase()})` : ''}`,
              promoted: true,
              rank: 2,
            },
            {
              label: 'DOB',
              value: patient.dob ?? 'Unknown',
              rank: 3,
            },
            {
              label: 'Phone',
              value: patient.phoneNumber ? formatPhoneNumber(patient.phoneNumber) : 'Unknown',
              rank: 4,
            },
            {
              label: 'Email',
              value: patient.email ?? 'Unknown',
              rank: 5,
            },
          ],
        },
      ],
      [patient, cdaDocument.detailSubsections],
    );

    return (
      <View minPresenceAhead={250}>
        <Text style={[headingStyles.h2, { marginBottom: 2 }]}>Document Details</Text>
        <Text style={headingStyles.subtitle}>
          This section contains information from when the document was originally created
        </Text>
        {cdaDocument.detailFields.length > 0 && (
          <PdfDocumentDetailItemsRenderer detailFields={cdaDocument.detailFields} />
        )}
        {orderBy(detailSubsections, 'rank').map((subsection) => (
          <View key={subsection.title} style={styles.detailsSubsectionContainer}>
            <Text style={[headingStyles.h2, { marginBottom: 2 }]}>{subsection.title}</Text>
            {subsection.subtitle && (
              <Text style={headingStyles.subtitle}>{subsection.subtitle}</Text>
            )}
            {isCdaDetailSubsectionTable(subsection) ? (
              <View style={tableStyles.table} wrap={true} minPresenceAhead={150}>
                {/* Header row */}
                <View
                  style={[tableStyles.rowFirstChild, { display: 'flex', alignItems: 'center' }]}
                  wrap={false}
                  minPresenceAhead={40}
                >
                  {subsection.columns.map((column) => (
                    <View
                      key={column}
                      style={[tableStyles.cell, { flex: 1 }]}
                      wrap={true}
                      minPresenceAhead={40}
                    >
                      <Text style={tableStyles.header}>{column.toUpperCase()}</Text>
                    </View>
                  ))}
                </View>

                {/* Data rows */}
                {subsection.rows.map((row) => (
                  <View
                    key={row.join('')}
                    style={tableStyles.rowNotFirstChild}
                    wrap={false}
                    minPresenceAhead={100}
                  >
                    {row.map((cell) => (
                      <View key={cell} wrap={true} style={[tableStyles.cell, { flex: 1 }]}>
                        <Text>{cell}</Text>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            ) : (
              <PdfDocumentDetailItemsRenderer detailFields={subsection.fields} />
            )}
          </View>
        ))}
      </View>
    );
  },
);

interface PdfDocumentDetailItemsRendererProps {
  detailFields: Array<CdaDetailField>;
}

const PdfDocumentDetailItemsRenderer = ({ detailFields }: PdfDocumentDetailItemsRendererProps) => {
  return (
    <View style={styles.detailItemsContainer}>
      {orderBy(detailFields, 'rank').map((detailField) => (
        <PdfDocumentDetailItemRenderer
          key={`detail-field-${detailField.label}`}
          detailField={detailField}
        />
      ))}
    </View>
  );
};

interface PdfDocumentDetailItemRendererProps {
  detailField: CdaDetailField;
}

const PdfDocumentDetailItemRenderer = ({ detailField }: PdfDocumentDetailItemRendererProps) => {
  return (
    <View minPresenceAhead={40} style={styles.detailItem} wrap={false}>
      <Text style={styles.detailItemLabel} wrap={false}>
        {detailField.label.toUpperCase()}
      </Text>
      <Text style={styles.detailItemValue} wrap={true}>
        {detailField.value}
      </Text>
    </View>
  );
};
